import * as React from 'react';
import styled, { css } from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { rem } from '../../tokens/spacing';

export type EmptyStateSize = 'sm' | 'md' | 'lg';

export interface EmptyStateProps extends React.HTMLAttributes<HTMLDivElement> {
  icon?: string;
  title: string;
  description?: string;
  action?: React.ReactNode;
  size?: EmptyStateSize;
  semanticId?: string;
}

const sizeStyles = {
  sm: css`
    gap: ${rem(8)};
    padding: ${rem(5)};
  `,
  md: css`
    gap: ${rem(10)};
    padding: ${rem(10)};
  `,
  lg: css`
    gap: ${rem(24)};
    padding: ${rem(48)} ${rem(16)};
  `,
};

const iconSizes = {
  sm: rem(20),
  md: rem(50),
  lg: rem(127),
};

const titleStyles = {
  sm: css`
    font-size: ${rem(12)};
    line-height: ${rem(14)};
    font-weight: 500;
  `,
  md: css`
    font-size: ${rem(14)};
    line-height: ${rem(18)};
    font-weight: 500;
  `,
  lg: css`
    font-size: ${rem(20)};
    line-height: ${rem(26)};
    font-weight: 600;
  `,
};

const shouldForward = (prop: string | number): boolean =>
  !['$size'].includes(String(prop));

const Root = styled.div.withConfig({ shouldForwardProp: shouldForward })<{
  $size: EmptyStateSize;
}>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-family: var(--ui-font-sans);
  ${({ $size }) => sizeStyles[$size]}
`;

const IconWrapper = styled.span.withConfig({ shouldForwardProp: shouldForward })<{
  $size: EmptyStateSize;
}>`
  font-size: ${({ $size }) => iconSizes[$size]};
  font-weight: 400;
  color: var(--ui-border, #D7D7D7);
  line-height: 1;
`;

const Title = styled.h3.withConfig({ shouldForwardProp: shouldForward })<{
  $size: EmptyStateSize;
}>`
  margin: 0;
  font-family: var(--ui-font-heading, var(--ui-font-sans));
  color: var(--ui-text-muted);
  ${({ $size }) => titleStyles[$size]}
`;

const Description = styled.h5`
  margin: 0;
  font-family: var(--ui-font-heading, var(--ui-font-sans));
  font-weight: 400;
  font-size: ${rem(15)};
  line-height: ${rem(19)};
  color: var(--ui-text-muted);
`;

const ActionSlot = styled.div``;

export const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(function EmptyState(
  { icon, title, description, action, size = 'lg', semanticId, ...rest },
  ref,
) {
  const semanticProps = getSemanticAttributes(semanticId);

  return (
    <Root ref={ref} $size={size} {...semanticProps} {...rest}>
      {icon != null && icon !== '' && (
        <IconWrapper className="material-symbols-outlined" $size={size} aria-hidden>
          {icon}
        </IconWrapper>
      )}
      <Title $size={size}>{title}</Title>
      {description != null && description !== '' && (
        <Description>{description}</Description>
      )}
      {action != null && <ActionSlot>{action}</ActionSlot>}
    </Root>
  );
});

EmptyState.displayName = 'EmptyState';
